import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useTeamMembers from "@babackapp/private-api-client/client/use-team-members";
import useTestAlerting from "@babackapp/private-api-client/client/use-test-alerting";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import FormMultiOptionsSelector from "@babackapp/react-hook-forms-components/form-multi-options-selector";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import SettingsFormContainer, {
  SettingsFormHeader,
  SettingsFormPageButtonBar,
  SettingsFormSectionHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const AlertsSettingsSchema = z.object({
  isEnabled: z.boolean(),
  recipientUserIds: z.array(z.string()),
  sendHour: z.number().int().min(0).max(23),
  staleReturnDays: z.number().int().min(1),
  receivedNotRefundedDays: z.number().int().min(1),
});

type AlertsSettingsFormValues = z.infer<typeof AlertsSettingsSchema>;

const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  key: i,
  label: `${String(i).padStart(2, "0")}:00`,
}));

export default function AlertsSettingsPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { data: members } = useTeamMembers(shop.id);
  const { mutateAsync: updatePreferences } = useUpdateShopPreferences(shop.id);
  const { mutateAsync: testAlerting, isPending: isTestPending } =
    useTestAlerting(shop.id);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState<{
    alertCount: number;
    sent: boolean;
  } | null>(null);

  const alerting = shop.preferences?.alerting;

  const form = useForm<AlertsSettingsFormValues>({
    resolver: zodResolver(AlertsSettingsSchema),
    defaultValues: {
      isEnabled: alerting?.isEnabled ?? false,
      recipientUserIds: alerting?.recipientUserIds ?? [],
      sendHour: alerting?.sendHour ?? 8,
      staleReturnDays: alerting?.staleReturnDays ?? 7,
      receivedNotRefundedDays: alerting?.receivedNotRefundedDays ?? 3,
    },
  });

  const teamMemberOptions = (members ?? []).map((m) => ({
    key: m.userId,
    label: m.user
      ? `${m.user.firstName ?? ""} ${m.user.lastName ?? ""} (${m.user.email})`.trim()
      : m.userId,
  }));

  const onSubmit = async (values: AlertsSettingsFormValues) => {
    await updatePreferences({ alerting: values });
  };

  const onTestAlerting = async () => {
    setTestResult(null);
    const result = await testAlerting(testEmail);
    if (result) setTestResult(result);
  };

  return (
    <SettingsFormContainer>
      <SettingsFormHeader
        title={t("settings.alerts.title")}
        description={t("settings.alerts.description")}
      />
      <Card>
        <CardContent className="pt-6 space-y-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <SettingsFormSectionHeader
                title={t("settings.alerts.general")}
                id="general"
              />

              <FormField
                control={form.control}
                name="isEnabled"
                render={({ field }) => (
                  <FormSwitch
                    label={t("settings.alerts.isEnabled.label")}
                    description={t("settings.alerts.isEnabled.description")}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="recipientUserIds"
                render={({ field }) => (
                  <FormMultiOptionsSelector
                    label={t("settings.alerts.recipients.label")}
                    description={t("settings.alerts.recipients.description")}
                    values={field.value}
                    options={teamMemberOptions}
                    onChange={field.onChange}
                    hideKey
                  />
                )}
              />

              <FormField
                control={form.control}
                name="sendHour"
                render={({ field }) => (
                  <FormOptionsSelector
                    label={t("settings.alerts.sendHour.label")}
                    description={t("settings.alerts.sendHour.description")}
                    value={field.value}
                    options={SEND_HOUR_OPTIONS}
                    onChange={(v) => field.onChange(Number(v))}
                  />
                )}
              />

              <SettingsFormSectionHeader
                title={t("settings.alerts.thresholds")}
                id="thresholds"
              />

              <FormField
                control={form.control}
                name="staleReturnDays"
                render={({ field }) => (
                  <FormInput
                    label={t("settings.alerts.staleReturnDays.label")}
                    description={t(
                      "settings.alerts.staleReturnDays.description",
                    )}
                    type="number"
                    min={1}
                    value={String(field.value)}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="receivedNotRefundedDays"
                render={({ field }) => (
                  <FormInput
                    label={t(
                      "settings.alerts.receivedNotRefundedDays.label",
                    )}
                    description={t(
                      "settings.alerts.receivedNotRefundedDays.description",
                    )}
                    type="number"
                    min={1}
                    value={String(field.value)}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                )}
              />

              <SettingsFormPageButtonBar />
            </form>
          </Form>

          <div className="border-t pt-8 space-y-4">
            <SettingsFormSectionHeader
              title={t("settings.alerts.test.title")}
              id="test"
            />
            <p className="text-sm text-muted-foreground">
              {t("settings.alerts.test.description")}
            </p>
            <div className="flex gap-3 items-center max-w-md">
              <Input
                type="email"
                placeholder={t("settings.alerts.test.emailPlaceholder")}
                value={testEmail}
                onChange={(e) => {
                  setTestEmail(e.target.value);
                  setTestResult(null);
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={!testEmail || isTestPending}
                onClick={onTestAlerting}
              >
                {isTestPending
                  ? t("settings.alerts.test.sending")
                  : t("settings.alerts.test.button")}
              </Button>
            </div>
            {testResult && (
              <p className="text-sm text-muted-foreground">
                {testResult.sent
                  ? t("settings.alerts.test.successWithAlerts", {
                      count: testResult.alertCount,
                    })
                  : t("settings.alerts.test.successNoAlerts")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </SettingsFormContainer>
  );
}
